/**
 * Carga un estudio completo desde IndexedDB (dicom_cache) hacia el visor.
 * Decodifica en paralelo con prioridad de serie activa (mismo scheduler que R2/WADO).
 */
import dicomParser from 'dicom-parser'
import {
  listCompleteSopsForStudy,
  listCompletePreviewSopsOrderedForStudy,
  getCombinedStudyCacheStats,
  getCachedInstance,
  toArrayBuffer,
} from '../db/dicomCache'
import { getStudyCatalog, upsertStudyCatalog, STUDY_SOURCE } from '../db/studyCatalog'
import { loadFromCacheBySop } from './loadDicomInstanceWithCache'
import { loadPreviewFromCacheBySop } from './loadPreviewInstance'
import { groupBy } from '../functions'
import { stampIsScoutOnFileItem } from './isScoutSeries'
import { stampNumberOfFramesOnFile } from './seriesImageCounts'
import {
  isStudyLoadCancelled,
  throwIfStudyLoadCancelled,
  createStudyLoadAbortError,
  STUDY_LOAD_SOURCE,
} from './studyLoadSource'
import { getManifestDownloadConcurrency } from './mobileDownloadProfile'
import { uniquifySeriesNumbers } from './uniquifySeriesNumbers'
import {
  buildSeriesSlots,
  createSeriesPriorityScheduler,
  createDownloadProgressTracker,
  estimateLoadedBytes,
} from './downloadSeriesScheduler'

function tag(image, key) {
  try {
    if (!image || !image.data || typeof image.data.string !== 'function') return ''
    return image.data.string(key) || ''
  } catch (e) {
    return ''
  }
}

function tagInt(image, key, fallback) {
  const s = tag(image, key)
  const n = parseInt(s, 10)
  return Number.isFinite(n) ? n : fallback
}

function hintsFromPreviewLayout(entry) {
  if (!entry) {
    return parseInstanceHints(null)
  }
  return {
    seriesUid: entry.seriesUid || '',
    seriesNumber: entry.seriesNumber || 0,
    instanceNumber: entry.instanceNumber || 0,
    patientName: '',
    patientId: '',
    studyDate: '',
    studyTime: '',
    studyDescription: '',
    modality: entry.modality || '',
    seriesDescription: entry.seriesDescription || '',
    previewUrl: entry.previewUrl || null,
  }
}

function buildPreviewLayoutMap(catalog) {
  const map = new Map()
  const layout = catalog && Array.isArray(catalog.previewLayout) ? catalog.previewLayout : []
  for (let i = 0; i < layout.length; i++) {
    const entry = layout[i]
    if (entry && entry.sop) map.set(String(entry.sop), entry)
  }
  return map
}

function yieldToUi() {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => resolve())
    } else {
      setTimeout(resolve, 0)
    }
  })
}

/**
 * Headers DICOM sin pixel data (rápido) para ordenar series/instancias.
 * @param {ArrayBuffer} buffer
 */
function parseInstanceHints(buffer) {
  const empty = {
    seriesUid: '',
    seriesNumber: 0,
    instanceNumber: 0,
    patientName: '',
    patientId: '',
    studyDate: '',
    studyTime: '',
    studyDescription: '',
    modality: '',
    seriesDescription: '',
  }
  if (!buffer) return empty
  try {
    const bytes = new Uint8Array(buffer)
    // Detener antes de Pixel Data
    const ds = dicomParser.parseDicom(bytes, { untilTag: 'x7fe00010' })
    return {
      seriesUid: ds.string('x0020000e') || '',
      seriesNumber: parseInt(ds.string('x00200011') || '0', 10) || 0,
      instanceNumber: parseInt(ds.string('x00200013') || '0', 10) || 0,
      patientName: ds.string('x00100010') || '',
      patientId: ds.string('x00100020') || '',
      studyDate: ds.string('x00080020') || '',
      studyTime: ds.string('x00080030') || '',
      studyDescription: ds.string('x00081030') || '',
      modality: ds.string('x00080060') || '',
      seriesDescription: ds.string('x0008103e') || '',
    }
  } catch (e) {
    return empty
  }
}

/**
 * Totales por serie. Si `knownTotals` viene de un índice previo (pasada rápida
 * sobre todos los SOP en IDB), se usa como denominador fijo y no crece con el
 * buffer parcial.
 * @param {object[]} items
 * @param {Map<string, number>|Record<string, number>|null} [knownTotals]
 */
function applySeriesTotals(items, knownTotals = null) {
  const known = knownTotals
    ? knownTotals instanceof Map
      ? knownTotals
      : new Map(Object.entries(knownTotals))
    : null
  const bySeries = new Map()
  items.forEach((it) => {
    const k = it.seriesInstanceUID || String(it.series && it.series.seriesNumber)
    bySeries.set(k, (bySeries.get(k) || 0) + 1)
  })
  items.forEach((it) => {
    const k = it.seriesInstanceUID || String(it.series && it.series.seriesNumber)
    if (!it.series) it.series = {}
    const fromKnown = known && known.has(k) ? Number(known.get(k)) || 0 : 0
    const fromBuffer = bySeries.get(k) || 0
    // Nunca bajar un total ya conocido; el buffer parcial solo sirve de piso.
    const prev = Number(it.series.totalImages) || 0
    const frames =
      Number(it.numberOfFrames) || Number(it.series.numberOfFrames) || 0
    // Monofile multiframe: total = frames (no el conteo de SOPs = 1)
    const fromFrames = frames > 1 ? frames : 0
    it.series.totalImages = Math.max(fromKnown, fromBuffer, prev, fromFrames)
    if (!it.series.instanceCount || it.series.instanceCount < it.series.totalImages) {
      it.series.instanceCount = it.series.totalImages
    }
  })
}

/**
 * Cuenta instancias por serie a partir del plan de carga (todos los SOP).
 * @param {{ seriesKey?: string, hints?: { seriesUid?: string, seriesNumber?: number } }[]} planned
 * @returns {Map<string, number>}
 */
function buildKnownSeriesTotals(planned) {
  const map = new Map()
  if (!Array.isArray(planned)) return map
  planned.forEach((row) => {
    const uid =
      (row && row.hints && row.hints.seriesUid) ||
      (row && row.seriesKey) ||
      ''
    const sn =
      row && row.hints && row.hints.seriesNumber != null
        ? String(row.hints.seriesNumber)
        : ''
    const keys = []
    if (uid) keys.push(String(uid))
    if (sn) keys.push(sn)
    if (!keys.length && row && row.seriesKey) keys.push(String(row.seriesKey))
    keys.forEach((k) => {
      map.set(k, (map.get(k) || 0) + 1)
    })
  })
  return map
}

export { applySeriesTotals, buildKnownSeriesTotals, uniquifySeriesNumbers }

/** Deja píxeles solo en la 1.ª instancia de cada serie (tras ordenar). */
function retainFirstOfSeriesPixels(items) {
  const seen = new Set()
  items.forEach((it) => {
    const k = it.seriesInstanceUID || String(it.series && it.series.seriesNumber)
    if (!seen.has(k)) {
      seen.add(k)
      // conservar image si ya está; si no, el viewer rehidrata por sop/imageId
    } else if (it.image) {
      it.image = null
    }
  })
}

function sortStudyItems(items) {
  items.sort((a, b) => {
    const sn = (a.series.seriesNumber || 0) - (b.series.seriesNumber || 0)
    if (sn !== 0) return sn
    return (a.instanceNumber || 0) - (b.instanceNumber || 0)
  })
}

function buildExplorerData(items) {
  const patientList = groupBy(items, (a) => a.patient.patientName)
  const patientKeys = [...patientList.keys()]
  return {
    folder: null,
    patient: { list: patientList, keys: patientKeys },
  }
}

/**
 * Primera serie del estudio (archivos ya ordenados).
 * @param {object[]} items
 */
export function firstSeriesFilesFromItems(items) {
  if (!Array.isArray(items) || items.length === 0) return []
  const sorted = items.slice()
  sortStudyItems(sorted)
  const firstSn =
    sorted[0] && sorted[0].series ? sorted[0].series.seriesNumber : null
  if (firstSn == null) return sorted.slice(0, 1)
  return sorted.filter(
    (it) => it && it.series && it.series.seriesNumber === firstSn
  )
}

/**
 * @param {string} studyInstanceUID
 * @param {{
 *   setFilesStore: (files: object[]|null) => void,
 *   onExplorerUpdate?: (data: object) => void,
 *   isOpenStore?: (v: object) => void,
 *   activeDcmIndex?: number,
 *   onPrepareNewStudy?: (uid: string) => void,
 *   catalogHint?: object,
 *   onProgress?: (p: { loaded: number, total: number, seriesReady: number }) => void,
 *   onDownloadProgress?: (stats: object) => void,
 *   getActiveSeriesIdx?: (() => number)|null,
 *   getPinnedSeriesIdx?: (() => number[])|null,
 *   getScrollFocus?: (() => { seriesIdx?: number, sliceIndex?: number, radius?: number }|null)|null,
 *   onLoadComplete?: (files: object[]) => void,
 *   compareMerge?: boolean — no limpia store ni prepareReplace (merge prior),
 *   skipClear?: boolean,
 * }} handlers
 */
export async function loadStudyFromLocalCache(studyInstanceUID, handlers) {
  const uid = String(studyInstanceUID || '')
  if (!uid) {
    throw new Error('StudyInstanceUID vacío')
  }
  const h = handlers || {}
  const signal = h.signal
  const isCancelled = h.isCancelled
  const cancelled = () => isStudyLoadCancelled(signal, isCancelled)
  const assertNotCancelled = () => throwIfStudyLoadCancelled(signal, isCancelled)
  const skipClear = !!h.skipClear || !!h.compareMerge

  if (!skipClear && typeof h.onPrepareNewStudy === 'function') {
    h.onPrepareNewStudy(uid)
  }

  // Asegurar viewport montado antes de limpiar/cargar
  if (typeof h.isOpenStore === 'function') {
    h.isOpenStore({
      index: typeof h.activeDcmIndex === 'number' ? h.activeDcmIndex : 0,
      value: true,
    })
  }

  // Limpiar estudio anterior de inmediato (explorer vacío → sin ansiedad de “¿cambió?”)
  // En compareMerge/skipClear el caller fusiona snapshots sin vaciar el store.
  if (!skipClear && typeof h.setFilesStore === 'function') {
    h.setFilesStore(null)
  }

  const sopSet = await listCompleteSopsForStudy(uid)
  let previewOnly = false
  let sops = Array.from(sopSet)
  if (sops.length === 0) {
    sops = await listCompletePreviewSopsOrderedForStudy(uid)
    previewOnly = sops.length > 0
  }
  if (sops.length === 0) {
    throw new Error('No hay instancias complete en caché para este estudio')
  }

  if (typeof h.onCacheMeta === 'function') {
    try {
      h.onCacheMeta({
        previewOnly,
        cacheKind: previewOnly ? 'preview' : 'dicom',
        instanceCount: sops.length,
      })
    } catch (e) {
      /* ignore */
    }
  }

  const catalog = (await getStudyCatalog(uid)) || h.catalogHint || {}
  const layoutBySop = buildPreviewLayoutMap(catalog)

  // Pasada rápida: ordenar por serie/instancia y priorizar 1.ª de cada serie (miniatura).
  // NO leer el blob completo: solo cabecera (≤256 KiB) y ceder el hilo cada N SOP.
  /** @type {{ sop: string, hints: ReturnType<typeof parseInstanceHints>, seriesKey: string }[]} */
  const scanned = []
  const HEADER_SLICE_BYTES = 256 * 1024
  for (let i = 0; i < sops.length; i++) {
    assertNotCancelled()
    const sop = sops[i]
    let hints = parseInstanceHints(null)
    if (previewOnly) {
      hints = hintsFromPreviewLayout(layoutBySop.get(sop))
    } else if (layoutBySop.has(sop)) {
      hints = hintsFromPreviewLayout(layoutBySop.get(sop))
    } else {
      try {
        const row = await getCachedInstance(sop)
        const blob = row && row.blob
        if (blob) {
          const slice =
            typeof blob.slice === 'function'
              ? blob.slice(0, Math.min(HEADER_SLICE_BYTES, blob.size || HEADER_SLICE_BYTES))
              : blob
          const buf = await toArrayBuffer(slice)
          hints = parseInstanceHints(buf)
        }
      } catch (e) {
        /* ignore */
      }
    }
    const seriesKey = hints.seriesUid || `sn:${hints.seriesNumber}` || sop
    scanned.push({ sop, hints, seriesKey })
    if (i > 0 && i % 6 === 0) {
      await yieldToUi()
    }
  }

  scanned.sort((a, b) => {
    const sn = (a.hints.seriesNumber || 0) - (b.hints.seriesNumber || 0)
    if (sn !== 0) return sn
    return (a.hints.instanceNumber || 0) - (b.hints.instanceNumber || 0)
  })

  const firstOfSeriesSeen = new Set()
  /** @type {{ sop: string, hints: ReturnType<typeof parseInstanceHints>, isFirstOfSeries: boolean, seriesKey: string }[]} */
  const planned = scanned.map((row) => {
    const isFirstOfSeries = !firstOfSeriesSeen.has(row.seriesKey)
    if (isFirstOfSeries) firstOfSeriesSeen.add(row.seriesKey)
    return { ...row, isFirstOfSeries }
  })

  const seriesKeyToIdx = new Map()
  /** @type {{ seriesNumber: number, description: string, totalTasks: number }[]} */
  const seriesInfo = []
  planned.forEach((row) => {
    if (!seriesKeyToIdx.has(row.seriesKey)) {
      const idx = seriesKeyToIdx.size
      seriesKeyToIdx.set(row.seriesKey, idx)
      seriesInfo.push({
        seriesNumber: row.hints.seriesNumber || idx + 1,
        description: String(row.hints.seriesDescription || '').trim(),
        totalTasks: 0,
      })
    }
    const sIdx = seriesKeyToIdx.get(row.seriesKey)
    seriesInfo[sIdx].totalTasks += 1
  })

  const tasks = planned.map((row, slot) => ({
    ...row,
    seriesIdx: seriesKeyToIdx.get(row.seriesKey),
    slot,
  }))

  const numSeries = seriesInfo.length
  const seriesSlots = buildSeriesSlots(tasks)
  const parallel = Math.max(1, getManifestDownloadConcurrency(tasks.length))
  const getActiveSeriesIdx =
    typeof h.getActiveSeriesIdx === 'function' ? h.getActiveSeriesIdx : () => 0
  const getPinnedSeriesIdx =
    typeof h.getPinnedSeriesIdx === 'function' ? h.getPinnedSeriesIdx : () => []
  const getScrollFocus =
    typeof h.getScrollFocus === 'function'
      ? h.getScrollFocus
      : () => ({
          seriesIdx: getActiveSeriesIdx(),
          sliceIndex: 0,
          radius: 2,
        })

  const scheduler = createSeriesPriorityScheduler({
    numSeries,
    seriesSlots,
    seriesInfo,
    parallel,
    getActiveSeriesIdx,
    getPinnedSeriesIdx,
    getScrollFocus,
  })

  const decodedFirstOfSeries = new Set()
  let seriesReady = 0
  const knownSeriesTotals = buildKnownSeriesTotals(planned)
  const orderedItems = new Array(tasks.length)
  let lastPublishAt = 0

  const lookupSeriesTotal = (seriesUid, seriesNumber) => {
    if (seriesUid && knownSeriesTotals.has(seriesUid)) {
      return knownSeriesTotals.get(seriesUid)
    }
    const sn = seriesNumber != null ? String(seriesNumber) : ''
    if (sn && knownSeriesTotals.has(sn)) return knownSeriesTotals.get(sn)
    return 0
  }

  const getSnapshot = () => {
    const filled = orderedItems.filter(Boolean)
    uniquifySeriesNumbers(filled)
    applySeriesTotals(filled, knownSeriesTotals)
    sortStudyItems(filled)
    return filled.slice()
  }

  const applyPublish = async () => {
    const snapshot = getSnapshot()
    if (snapshot.length === 0) return
    lastPublishAt = Date.now()
    if (typeof h.setFilesStore === 'function') {
      h.setFilesStore(snapshot)
    }
    if (typeof h.onExplorerUpdate === 'function') {
      h.onExplorerUpdate(buildExplorerData(snapshot))
    }
    if (typeof h.isOpenStore === 'function') {
      h.isOpenStore({
        index: typeof h.activeDcmIndex === 'number' ? h.activeDcmIndex : 0,
        value: true,
      })
    }
    await yieldToUi()
  }

  const publish = async (force) => {
    if (cancelled()) return
    // Como 0.20.60: publicar con throttle (no congelar mid-gesto)
    const now = Date.now()
    if (!force && now - lastPublishAt < 120) return
    await applyPublish()
  }

  const emitLegacyProgress = (stats) => {
    if (typeof h.onProgress === 'function') {
      try {
        h.onProgress({
          loaded: stats.completed,
          total: stats.totalTasks,
          seriesReady,
        })
      } catch (e) {
        /* ignore */
      }
    }
  }

  const progressTracker = createDownloadProgressTracker({
    totalTasks: tasks.length,
    seriesInfo,
    loadSource: STUDY_LOAD_SOURCE.LOCAL,
    throttleMs: 220,
    onProgress: (stats) => {
      if (cancelled()) return
      emitLegacyProgress(stats)
      if (typeof h.onDownloadProgress === 'function') {
        try {
          h.onDownloadProgress(stats)
        } catch (e) {
          /* ignore */
        }
      }
    },
    extraStats: () => ({
      studyInstanceUID: uid,
      localPhase: 'load',
    }),
  })

  progressTracker.emit(true)

  const buildSkeletonItem = (row, slot) => {
    const { sop, hints } = row
    const seriesUid = hints.seriesUid || row.seriesKey || 'unknown-series'
    const seriesNumber = hints.seriesNumber || 0
    const instanceNumber = hints.instanceNumber || slot + 1
    const knownTotal = lookupSeriesTotal(seriesUid, seriesNumber) || 0
    return {
      imageId: previewOnly ? null : `local-cache:${sop}`,
      instanceNumber,
      name: previewOnly ? `${instanceNumber}.jpg` : `${instanceNumber}.dcm`,
      image: null,
      sopInstanceUID: sop,
      seriesInstanceUID: seriesUid,
      fromCache: true,
      skeleton: true,
      renderKind: previewOnly ? 'preview' : 'dicom',
      patient: {
        patientName: hints.patientName || catalog.patientName || 'Unknown Patient',
        patientId: hints.patientId || catalog.patientId || 'N/A',
      },
      study: {
        studyId: uid,
        studyDate: hints.studyDate || catalog.studyDate || '',
        studyTime: hints.studyTime || '',
        studyDateTime:
          hints.studyDate || catalog.studyDate
            ? `${hints.studyDate || catalog.studyDate}.${hints.studyTime || ''}`
            : uid,
        studyDescription: hints.studyDescription || catalog.description || '',
      },
      series: {
        seriesDate: '',
        seriesTime: '',
        seriesDescription: hints.seriesDescription || '',
        seriesNumber,
        seriesInstanceUID: seriesUid,
        echoNumber: 0,
        totalImages: knownTotal,
        instanceCount: knownTotal,
        modality: hints.modality || catalog.modality || '',
      },
    }
  }

  // Stubs en Redux YA: Explorer + viewport scrolleable sin esperar decode IDB
  for (let s = 0; s < tasks.length; s++) {
    orderedItems[s] = buildSkeletonItem(tasks[s], s)
  }
  try {
    await publish(true)
  } catch (eSkel) {
    console.warn('[loadStudyFromLocalCache] skeleton publish', eSkel)
  }
  if (typeof h.onStackSeeded === 'function') {
    try {
      h.onStackSeeded({
        totalTasks: tasks.length,
        previewOnly,
        loadSource: 'local',
      })
    } catch (eSeed) {
      /* ignore */
    }
  }

  const buildItem = (row, slot, loaded, image, keepPixels) => {
    const { sop, hints } = row
    const seriesUid =
      tag(image, 'x0020000e') || hints.seriesUid || row.seriesKey || 'unknown-series'
    const patientName =
      tag(image, 'x00100010') ||
      hints.patientName ||
      catalog.patientName ||
      'Unknown Patient'
    const patientId =
      tag(image, 'x00100020') || hints.patientId || catalog.patientId || 'N/A'
    const studyDate =
      tag(image, 'x00080020') || hints.studyDate || catalog.studyDate || ''
    const studyTime = tag(image, 'x00080030') || hints.studyTime || ''
    const studyDescription =
      tag(image, 'x00081030') ||
      hints.studyDescription ||
      catalog.description ||
      ''
    const seriesNumber =
      tagInt(image, 'x00200011', hints.seriesNumber || 0) || hints.seriesNumber || 0
    const instanceNumber =
      tagInt(image, 'x00200013', hints.instanceNumber || slot + 1) ||
      hints.instanceNumber ||
      slot + 1
    const modality =
      tag(image, 'x00080060') || hints.modality || catalog.modality || ''
    const numberOfFrames = tagInt(image, 'x00280008', 0) || 0
    const knownTotal = lookupSeriesTotal(seriesUid, seriesNumber) || 0
    const totalImages =
      numberOfFrames > 1
        ? Math.max(knownTotal, numberOfFrames)
        : knownTotal

    const item = {
      imageId: loaded.imageId,
      instanceNumber,
      name: `${instanceNumber}.dcm`,
      image, // siempre en RAM para onInstanceReady; strip si !keepPixels
      sopInstanceUID: loaded.sopInstanceUID || sop,
      seriesInstanceUID: seriesUid,
      fromCache: true,
      skeleton: false,
      numberOfFrames: numberOfFrames > 0 ? numberOfFrames : undefined,
      patient: { patientName, patientId },
      study: {
        studyId: uid,
        studyDate,
        studyTime,
        studyDateTime: studyDate
          ? `${studyDate}.${studyTime || ''}`
          : uid,
        studyDescription,
      },
      series: {
        seriesDate: tag(image, 'x00080021'),
        seriesTime: tag(image, 'x00080031'),
        seriesDescription:
          tag(image, 'x0008103e') || hints.seriesDescription || '',
        seriesNumber,
        seriesInstanceUID: seriesUid,
        echoNumber: tagInt(image, 'x00180086', 0),
        totalImages,
        instanceCount: totalImages,
        modality,
        imageType: tag(image, 'x00080008') || undefined,
      },
    }
    if (numberOfFrames > 1) {
      stampNumberOfFramesOnFile(item, numberOfFrames)
    }
    stampIsScoutOnFileItem(item)
    if (!keepPixels) {
      // Conservar pixels solo para notify; el store se aliviana abajo
      item._notifyImage = image
      item.image = null
    }
    return item
  }

  const buildPreviewItem = (row, slot, loaded, keepPixels) => {
    const { sop, hints } = row
    const seriesUid = hints.seriesUid || row.seriesKey || 'unknown-series'
    const patientName = hints.patientName || catalog.patientName || 'Unknown Patient'
    const patientId = hints.patientId || catalog.patientId || 'N/A'
    const studyDate = hints.studyDate || catalog.studyDate || ''
    const studyTime = hints.studyTime || ''
    const studyDescription =
      hints.studyDescription || catalog.description || ''
    const seriesNumber = hints.seriesNumber || 0
    const instanceNumber = hints.instanceNumber || slot + 1
    const modality = hints.modality || catalog.modality || ''
    const knownTotal = lookupSeriesTotal(seriesUid, seriesNumber) || 0

    return {
      imageId: loaded.imageId,
      instanceNumber,
      name: `${instanceNumber}.jpg`,
      image: keepPixels ? loaded.image : null,
      sopInstanceUID: loaded.sopInstanceUID || sop,
      seriesInstanceUID: seriesUid,
      fromCache: true,
      skeleton: false,
      previewUrl: hints.previewUrl || null,
      renderKind: 'preview',
      patient: { patientName, patientId },
      study: {
        studyId: uid,
        studyDate,
        studyTime,
        studyDateTime: studyDate ? `${studyDate}.${studyTime || ''}` : uid,
        studyDescription,
      },
      series: {
        seriesDate: '',
        seriesTime: '',
        seriesDescription: hints.seriesDescription || '',
        seriesNumber,
        seriesInstanceUID: seriesUid,
        echoNumber: 0,
        totalImages: knownTotal,
        instanceCount: knownTotal,
        modality,
      },
    }
  }

  const loadOne = async (row) => {
    const { sop, hints, seriesKey } = row
    if (previewOnly) {
      const loaded = await loadPreviewFromCacheBySop(sop)
      if (!loaded || !loaded.image) {
        return { ok: false, bytes: 0, keepPixels: false }
      }
      const seriesUid = hints.seriesUid || seriesKey || 'unknown-series'
      const keepPixels = !decodedFirstOfSeries.has(seriesUid)
      if (keepPixels) {
        decodedFirstOfSeries.add(seriesUid)
        seriesReady += 1
      }
      return {
        ok: true,
        bytes: loaded.image && loaded.image.sizeInBytes ? loaded.image.sizeInBytes : 0,
        keepPixels,
        item: buildPreviewItem(row, row.slot, loaded, keepPixels),
      }
    }

    const loaded = await loadFromCacheBySop(sop, uid)
    if (!loaded || !loaded.image) {
      return { ok: false, bytes: 0, keepPixels: false }
    }

    const image = loaded.image
    const seriesUid =
      tag(image, 'x0020000e') || hints.seriesUid || seriesKey || 'unknown-series'
    const keepPixels = !decodedFirstOfSeries.has(seriesUid)
    if (keepPixels) {
      decodedFirstOfSeries.add(seriesUid)
      seriesReady += 1
    }

    return {
      ok: true,
      bytes: estimateLoadedBytes(image),
      keepPixels,
      item: buildItem(row, row.slot, loaded, image, keepPixels),
    }
  }

  async function worker() {
    while (!scheduler.isDone() && scheduler.completed.size < tasks.length) {
      if (cancelled()) return

      // Fase A: decode/fetch sin pausa por isInteracting (P0 via getScrollFocus)
      const slot = scheduler.pickNextSlot()
      if (slot == null) {
        if (scheduler.completed.size >= tasks.length) return
        await new Promise((r) => setTimeout(r, 2))
        continue
      }

      const task = tasks[slot]
      let ok = false
      let bytes = 0
      let keepPixels = false

      try {
        assertNotCancelled()
        const result = await loadOne(task)
        ok = !!(result && result.ok)
        bytes = (result && result.bytes) || 0
        keepPixels = !!(result && result.keepPixels)
        if (result && result.item) {
          orderedItems[slot] = result.item
          if (typeof h.onInstanceReady === 'function') {
            const img =
              result.item.image || result.item._notifyImage || null
            if (img) {
              try {
                const { _notifyImage, ...rest } = result.item
                h.onInstanceReady(
                  { ...rest, image: img },
                  { seriesIdx: task.seriesIdx, slot }
                )
              } catch (eReady) {
                /* ignore */
              }
            }
            if (result.item._notifyImage) {
              delete result.item._notifyImage
            }
          }
        }
      } catch (e) {
        if (e && e.name === 'AbortError' && cancelled()) return
        /* ignore single instance failure */
      } finally {
        scheduler.finishSlot(slot)
      }

      if (cancelled()) return

      progressTracker.recordTaskDone({
        seriesIdx: task.seriesIdx,
        ok,
        bytes,
      })

      const { totalDone } = progressTracker.totals
      if (keepPixels || totalDone % 8 === 0 || totalDone === tasks.length) {
        await publish(keepPixels || totalDone === tasks.length)
      }
      if (totalDone % 5 === 0 || keepPixels) {
        progressTracker.emit(false)
      }
    }
  }

  if (tasks.length > 0) {
    await Promise.all(Array.from({ length: parallel }, () => worker()))
  }

  if (cancelled()) {
    throw createStudyLoadAbortError()
  }

  progressTracker.emit(true)

  const items = getSnapshot()

  if (items.length === 0) {
    throw new Error('No se pudo decodificar ninguna instancia del caché local')
  }

  retainFirstOfSeriesPixels(items)

  const sample = items.find((it) => it.image) || items[0]
  const stats = await getCombinedStudyCacheStats(uid)
  assertNotCancelled()
  await upsertStudyCatalog({
    studyInstanceUID: uid,
    patientName: sample.patient.patientName,
    patientId: sample.patient.patientId,
    studyDate: sample.study.studyDate,
    modality: (sample.series && sample.series.modality) || catalog.modality || '',
    description: sample.study.studyDescription,
    instanceCount: stats.cachedComplete || items.length,
    bytesTotal: stats.bytes,
    source: catalog.source || STUDY_SOURCE.UNKNOWN,
    cacheKind: previewOnly ? 'preview' : stats.dicomComplete > 0 ? 'dicom' : 'preview',
    previewLayout: catalog.previewLayout,
    edgeId: catalog.edgeId || undefined,
    clinicName: catalog.clinicName || undefined,
  })

  assertNotCancelled()
  await publish(true)

  if (typeof h.onLoadComplete === 'function') {
    try {
      h.onLoadComplete(items.slice())
    } catch (e) {
      console.warn('[loadStudyFromLocalCache] onLoadComplete', e)
    }
  }

  return {
    files: items,
    studyInstanceUID: uid,
    count: items.length,
    previewOnly,
    cacheKind: previewOnly ? 'preview' : 'dicom',
  }
}
